import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBenefitSummary, selectBenefitSummary } from '../../../redux/WithcookieSlice';
import { BenefitSummaryProps } from './BenefitSummary.types';
import { adaptBenefitSummaryProps } from './BenefitSummary.adapter';
import './BenefitSummary.css';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * 날짜 형식화 함수
 */
function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * 한국어 날짜 형식화 함수
 */
function formatKoreanDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/**
 * BenefitSummary 컴포넌트
 */
const BenefitSummaryComponent: React.FC<any> = (props) => {
  const adaptedProps = adaptBenefitSummaryProps(props);
  const {
    style = {},
    className = '',
    componentProps = {},
    isPreview = false,
    id = '',
    ...rest
  } = adaptedProps;

  // Redux 타입 에러 해결을 위해 any 타입 단언 사용
  const dispatch = useDispatch() as any;
  
  // Redux store에서 혜택 요약 데이터 가져오기
  const benefitSummaryData = useSelector(selectBenefitSummary);

  // 컴포넌트 내부 상태
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // API 호출 중복 방지 ref
  const fetchBenefitSummaryRef = useRef(false);

  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();

  useEffect(() => {
    // 이미 API 호출 중이면 중복 방지
    if (fetchBenefitSummaryRef.current) {
      return;
    }
    
    // 날짜 범위 계산 (현재부터 30일 전까지)
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - 30);

    const from = formatDate(fromDate);
    const to = formatDate(toDate);

    // API 호출 시작 표시
    fetchBenefitSummaryRef.current = true;

    // Redux 액션 디스패치로 데이터 로드
    dispatch(fetchBenefitSummary({ from, to }))
      .unwrap()
      .then(() => {
        setLoading(false);
      })
      .catch((error: any) => {
        console.error("Error fetching benefit summary:", error);
        setLoading(false);
      })
      .finally(() => {
        fetchBenefitSummaryRef.current = false;
      });
  }, [dispatch]);

  // Redux store의 데이터가 변경되면 컴포넌트 상태 업데이트
  useEffect(() => {
    if (benefitSummaryData) {
      setItems(benefitSummaryData.items || []);
      setLoading(benefitSummaryData.loading);
    }
  }, [benefitSummaryData]);

  // 컴포넌트 속성에서 제목 가져오기 - 다국어 처리 적용
  const title = componentProps.title ? t(componentProps.title) : t('최근 1개월 요약');
  const emptyText = componentProps.emptyText ? t(componentProps.emptyText) : t('데이터가 없습니다');

  // 합계 계산
  const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);

  // 로딩 중 표시
  if (loading) {
    return (
      <div className={`benefit-summary-container ${className}`} style={style} {...rest}>
        <h2 className="benefit-summary-title">{t(title)}</h2>
        <div className="benefit-summary-loading">
          <div className="benefit-summary-spinner"></div>
          {t('로딩 중...')}
        </div>
      </div>
    );
  }

  // 에러 표시
  if (benefitSummaryData.error) {
    return (
      <div className={`benefit-summary-container ${className}`} style={style} {...rest}>
        <h2 className="benefit-summary-title">{t(title)}</h2>
        <div className="benefit-summary-error">
          {t('데이터를 불러오는데 실패했습니다.')} {benefitSummaryData.error}
        </div>
      </div>
    );
  }

  return (
    <div className={`benefit-summary-container ${className}`} style={style} {...rest}>
      <h2 className="benefit-summary-title">{t(title)}</h2>
      
      <div className="benefit-summary-table-container">
        {/* 테이블 헤더 */}
        <div className="benefit-summary-header">
          <div className="bs-col date">{t('날짜')}</div>
          <div className="bs-col category">{t('카테고리')}</div>
          <div className="bs-col amount">{t('금액')}</div>
        </div>
        
        {/* 테이블 내용 */}
        {items.length === 0 ? (
          <div className="benefit-summary-empty">{t(emptyText)}</div>
        ) : (
          <>
            {items.map((item) => (
              <div key={item.id} className="benefit-summary-item">
                <div className="bs-col date">{formatKoreanDate(item.summaryDate)}</div>
                <div className="bs-col category">{item.extraPresetStep?.name || '-'}</div>
                <div className="bs-col amount">{item.amount?.toLocaleString() || '0'}</div>
              </div>
            ))}
            
            {/* 테이블 푸터 - 합계 */}
            {/* <div className="benefit-summary-footer">
              <div className="bs-col total-label">{t('합계')}</div>
              <div className="bs-col total-amount">{totalAmount.toLocaleString()}</div>
            </div> */}
          </>
        )}
      </div>
    </div>
  );
};

export default BenefitSummaryComponent; 